import {
  BreifInfluencerV2StatusEnum,
  SORT_ORDER,
} from "@entities/brief/lib/constants";
import { IUser } from "./brandUser";
import { BreifInfluencerV2MainStatusEnum } from "./brief";
import { IBrandComment } from "./briefInfluencer";
import { IDelPackage } from "./deliverableConfigurator";
import { InfluencerContact } from "./influencer";
import { IPagination } from "./user";

export interface ISocialMedia {
  _id: string;
  username: string;
  profilePicUrl: string;
  platform?: string;
  link?: string;
  searchV1?: {
    isVerified?: boolean;
  };
}

export interface IInfluencerCostRequest {
  briefInfluencerId: string;

  cost: number;
  note?: string;
}
export interface IBrandCostRequest {
  briefInfluencerId: string;
  cost: number;
  note: string;
  isNonCancellable: boolean;
}

export interface INegotiateCost {
  briefInfluencerId: string;
  targetBrandCost: number;

  remarks: string;
}
export interface INegotiateInfluencerCost {
  briefInfluencerId: string;
  targetInfluencerCost: number;
}
export interface IConfirmationRequest {
  briefInfluencerId: string;
  note: number;
  paymentTerms: string;
  cost: string;
  confirmationNotes?: string;
}

export interface IDecline {
  reason: string;
  briefInfluencerId: string;
}

export interface ISkipConfirmationRequest {
  briefInfluencerIds: string[];
}

export interface IInfluencerContact extends InfluencerContact {}

export interface IInfluencer {
  _id: string;
  firstName: string;
  lastName: string;
  influencerId: string;
  type?: string;
  isPrimaryContact?: boolean;
  contacts?: IInfluencerContact[];
  isAgency?: boolean;
  agency?: {
    _id: string;
    name: string;
    companyName: string;
    profilePicUrl: string;
  };
  location?: string[];
  avgReview?: number;
}

export interface ICreatedBy {
  _id: string;
  firstName: string;
  lastName: string;
}

export interface IDeliverable {
  category: string;
  type: string;
  isContentRights: boolean;
  contentRightsDurations: string;
}

export interface ICost {
  cost: number;
  createdBy: ICreatedBy;
  createdByModelName: string;
  createdByType: string;
  costType: string;
  createdAt: string;
  note?: string;
  influencerCost?: ICost;
  revenue?: number;
  marginPercentage?: number;
  brandCpv?: number;
  influencerCpv?: number;
  isNonCancellable: boolean;
}
export enum TimelineEventType {
  InfluencerInitialCost = "Influencer-Initial-Cost",
  BrandInitialCost = "Brand-Initial-Cost",
  NewBrandCost = "New-Brand-Cost",
  NewInfluencerCost = "New-Influencer-Cost",
  NegotiationsRequested = "Negotiations-Requested",
  InitialInfluencerCostAsked = "Initial-Influencer-Cost-Asked",
  InitialInfluencerCostAskedTarget = "Initial-Influencer-Cost-Asked-Target",
  InitialInfluencerCostAccepted = "Initial-Influencer-Cost-Accepted",
  InitialInfluencerCostSubmitted = "Initial-Influencer-Cost-Submitted",
  InfluencerNegotiationsRequested = "Influencer-Negotiations-Requested",
  InfluencerNegotiationsAccepted = "Influencer-Negotiations-Accepted",
  NegotiationsRequestedInfluencer = "Negotiations-Requested-Influencer",
  DeliverableAttached = "Deliverable-Attached",
}
export interface ITimelineEvent {
  type: TimelineEventType;
  createdBy: ICreatedBy;
  createdByModelName: string;
  createdByType: string;
  createdAt: string;
  influencerCost?: ICost;
  brandCost?: ICost;
  targetBrandCost?: ICost;
  targetInfluencerCost?: ICost;
  targetRemarks?: string;
  tips?: string;
}

export interface IUsedByBriefInfluencer {
  _id: string;
  socialMedia: ISocialMedia;
  influencer: {
    _id: string;
    firstName: string;
    lastName: string;
    influencerId: string;
  };
  briefInfluencerId: string;
}

export interface IBriefDeliverable extends IDelPackage {
  _id?: string;
  brief?: string;
  briefId: string;
  brand?: string;
  brandId?: string;
  briefInfluencerIds?: string[];
  createdBy?: ICreatedBy;
  createdByModelName?: string;
  createdByType?: string;
  updatedBy?: string;
  updatedByModelName?: string;
  updatedByType?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IBriefDeliverableResponse {
  deliverableV2: IBriefDeliverable;
  countUsed: number;
  usedByBriefInfluencers: IUsedByBriefInfluencer[];
  countUsedByBriefInfluencersWithPurchased?: number;
}

export interface ICreareIBriefDeliverable extends IBriefDeliverable {}
export interface IEditIBriefDeliverable extends IBriefDeliverable {
  deliverableV2Id: string;
}

export enum CreatorProfileJobStatus {
  BasicInsightsNotEnabled = "BASIC_INSIGHTS_NOT_ENABLED",
  BasicInsightsFailed = "BASIC_INSIGHTS_FAILED",
  CollectMediaFailed = "COLLECT_MEDIA_FAILED",
  CollectMediaTimeout = "COLLECT_MEDIA_TIMEOUT",
  PlatformNotSupported = "PLATFORM_NOT_SUPPORTED",
  UserNotFound = "USER_NOT_FOUND",
  UserNoContent = "USER_NO_CONTENT",
  UserInsufficientContent = "USER_INSUFFICIENT_CONTENT",
  NoMedias = "NO_MEDIAS",
  InsufficientDownloads = "INSUFFICIENT_DOWNLOADS",
  AggregationFailed = "AGGREGATION_FAILED",
  PipelineError = "PIPELINE_ERROR",
}

export enum BriefFitErrorCodes {
  BriefNotFound = "BRIEF_NOT_FOUND",
  FitCheckDisabled = "FIT_CHECK_DISABLED",
  PlatformNotSupported = "PLATFORM_NOT_SUPPORTED",
  BasicDataFailed = "BASIC_DATA_FAILED",
  AudienceDataFailed = "AUDIENCE_DATA_FAILED",
  CreatorProfileJobFailed = "CREATOR_PROFILE_JOB_FAILED",
  CreatorProfileFailed = "CREATOR_PROFILE_FAILED",
  EvaluationFailed = "EVALUATION_FAILED",
}

export interface ICreatorProfileJob {
  _id: string;
  socialMediaObjectId: string;
  version: number;
  platform: string;
  status: string;
  failure?: {
    code: CreatorProfileJobStatus;
    message: string;
    retryable: boolean;
  };
  config: {
    maxPosts: number;
    minUploads: number;
    aggregateCount: number;
  };
  // progress: ICreatorProfileJobProgress;
  creatorProfileId?: string;
  geminiTriggered?: boolean;
  geminiStartedAt?: string;
  lockedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IBriefFitEvaluation {
  aiAnalysis?: {
    creator_campaign_fit: {
      creator_name: string;
      overall_score: number;
      verdict: string;
      recommendation: string;
      one_line_summary: string;
      frontend_summary: string;
    };
    score_breakdown: {
      creator_profile_fit: {
        score: number;
        reason: string;
      };
      audience_fit: {
        score: number;
        reason: string;
      };
      content_fit: {
        score: number;
        reason: string;
      };
      brand_integration_fit: {
        score: number;
        reason: string;
      };
      performance_signal: {
        score: number;
        reason: string;
      };
      safety_fit: {
        score: number;
        reason: string;
      };
      confidence_quality: {
        score: number;
        reason: string;
      };
    };
    fit_reasons: {
      top_match_reasons: string[];
      top_mismatch_reasons: string[];
      strongest_evidence: string[];
      weakest_evidence: string[];
    };
    audience_match: {
      audience_fit_summary: string;
      age_match: {
        score: number;
        campaign_need: string;
        creator_data: string;
        reason: string;
      };
      gender_match: {
        score: number;
        campaign_need: string;
        creator_data: string;
        reason: string;
      };
      location_match: {
        score: number;
        campaign_need: string;
        creator_data: string;
        reason: string;
      };
      interest_match: {
        score: number;
        campaign_need: string;
        creator_data: string;
        reason: string;
      };
    };
    creator_profile_match: {
      creator_category_match: string;
      creator_language_match: string;
      creator_location_match: string;
      creator_gender_match: string;
      creator_persona_match: string;
      creator_archetype_fit: string;
    };
    campaign_usage_recommendation: {
      best_use_case: string;
      recommended_content_angle: string;
      best_integration_style: string;
      why_this_use_case: string;
    };
    risk_assessment: {
      overall_risk_level: string;
      brand_safety_risks: string[];
      audience_mismatch_risks: string[];
      content_mismatch_risks: string[];
      performance_risks: string[];
      risk_summary: string;
    };
    creator_metrics_snapshot: {
      followers: number;
      avg_reel_views: number;
      engagement_rate: number;
      real_follower_percentage: number;
      verified: boolean;
      top_audience_cities: string[];
      dominant_audience_age_bucket: string;
      dominant_audience_gender: string;
    };
    frontend_highlights: {
      positive_badges: string[];
      warning_badges: string[];
      best_fit_labels: string[];
      not_fit_labels: string[];
    };
    final_decision_context: {
      should_brand_shortlist: boolean;
      decision_reason: string;
      what_to_check_manually: string[];
      confidence: string;
    };
  };
  failure?: {
    code: BriefFitErrorCodes;
    message: string;
    retryable: boolean;
  };
  attempt: number;
  briefId: string;
  _id: string;
  completedAt: string;
  updatedAt: string;
  creatorProfileId: string;
  creatorProfileJob: ICreatorProfileJob;
  creatorProfileJobId: string;
  creatorProfileVersion: number;
  evaluatingStartedAt: string;
  platform: string;
  socialMediaObjectId: string;
  status: BriefSocialMediaAiFitCheckStatus;
  username: string;
  aiWorkspaceMasterPromptId?: string;
  aiWorkspaceMasterPrompt?: string;
}

export interface IBriefInfluencerV2 {
  // briefSocialMediaAiFitCheck?: {
  //   _id: string;
  //   briefId: string;
  //   socialMediaObjectId: string;
  //   platform: string;
  //   username: string;
  //   status: string;
  //   createdAt: string;
  //   updatedAt: string;
  //   __v: number;
  //   basicDataStatus: string;
  //   audienceDataStatus: string;
  //   socialMediaUserAiAnalysisId: string;
  //   socialMediaUserAiAnalysisStatus: string;
  //   failedReason: string;
  // };
  briefFitEvaluationJob: IBriefFitEvaluation | null;

  isCostCheckedByPricing?: boolean;
  isQueuedForPurchaseLot?: boolean;
  mainStatus: BreifInfluencerV2MainStatusEnum;
  _id: string;
  brief: { name: string; _id: string };
  briefId: string;
  brand: { name: string; _id: string };
  brandId: string;
  socialMediaObjectId: string;
  socialMedia: ISocialMedia;
  influencerName: string;
  socialMediaName: string;
  isCreatorOnboardPending: boolean;
  influencerId: string;
  influencer: IInfluencer;
  followers: number;
  estViews: number;
  status: string;
  isCreatorDeclined: boolean;
  isPriceRequested: boolean;
  isPrimaryContactAdded: boolean;
  isBrandDeclined: boolean;
  isDeliverablesPending: boolean;
  isInitialInfluencerCostPending: boolean;
  isInitialBrandCostPending: boolean;
  isBrandNewCostPending: boolean;
  isInfluencerNewCostPending: boolean;
  isNewCommericalRequested: boolean;
  isConfirmationSent: boolean;
  isConfirmed: boolean;
  isNonCancellable?: boolean;
  timeline: ITimelineEvent[];
  brandTimeLine: ITimelineEvent[];
  createdBy: ICreatedBy;
  updatedBy: string;
  createdByModelName: string;
  updatedByModelName: string;
  createdByType: string;
  updatedByType: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  briefInfluencerId: string;
  // deliverable?: IBriefDeliverableResponse;
  deliverableV2?: IBriefDeliverable;
  deliverableV2Id?: string;
  primaryContact?: IInfluencerContact;
  influencerCost?: ICost;
  brandCost?: ICost;
  targetBrandCost?: ICost;
  targetInfluencerCost?: ICost;
  targetRemarks?: string;
  brandDeclinedReason?: string;
  creatorDeclinedReason?: string;
  paymentTerms?: string;
  dataFields: {
    [key: string]: string | number;
  };
  assignedPricingPoc?: IAssignedPOC;
  isInfluencerCostAsked?: boolean;
  isBrandFavourite?: boolean;
  brandComments?: IBrandComment[];
  offerStatus?: string;
  brandCostStatus?: string;
  pricingNotes?: string;
  assignedPricingPocV2: {
    assignedToUserId: string;
    assignedToUser: Pick<
      IUser,
      | "_id"
      | "emailId"
      | "firstName"
      | "lastName"
      | "phoneNumber"
      | "countryCode"
      | "profilePicUrl"
    >;
    assignedToModelName: string;
    assignedToType: string;
    assignedByUserId: string;
    assignedByUser: Pick<
      IUser,
      | "_id"
      | "emailId"
      | "firstName"
      | "lastName"
      | "phoneNumber"
      | "countryCode"
      | "profilePicUrl"
    >;
    assignedByModelName: string;
    assignedByType: string;
    deadlineFrom: string;
    deadlineTo: string;
    isPurchaseWithPrice: boolean;
    status: string;
    assignedAt: string;
  };
  pricingWhatsappReachout?: IBriefInfluencerWhatsappReachout;
  confirmationNotes?: string;
  agencySharedBrandNotes?: string;
  brandNotes?: string;
  isShortlisted?: boolean;

  pendingReviewActions?: {
    brand?: Partial<{
      agencySuggested: boolean;
      agencyDeclined: boolean;
      agencyAddedNotes: boolean;
      agencyAddNegoCost: boolean;
    }>;
    agency?: Partial<{
      brandShortlisted: boolean;
      brandDeclined: boolean;
      brandSuggested: boolean;
      brandPurchased: boolean;
      brandNegoRequested: boolean;
      brandChangedDeliverable: boolean;
      brandAddedNotes: boolean;
    }>;
  };
}
export enum BriefSocialMediaAiFitCheckAnalysisDataStatus {
  "Top Match" = "Top Match",
  Excellent = "Excellent",
  Strong = "Strong",
  Relevant = "Relevant",
}

export enum BriefSocialMediaAiFitCheckStatus {
  // "Not-Started" = "Not-Started",
  // Pending = "Pending",
  // Processing = "Processing",
  // Completed = "Completed",
  // Failed = "Failed",

  WaitingPrerequisites = "waiting_prerequisites",
  WaitingCreatorProfile = "waiting_creator_profile",
  Evaluating = "evaluating",
  Completed = "completed",
  Failed = "failed",
}

export interface IAssignedPOC {
  assignedTo: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  assignedToModelName: string;
  assignedToType: string;
  assignedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  assignedByModelName: string;
  assignedByType: string;
  assignedAt: string;
}

export interface IBriefInfluencerSearchRequestV2 {
  searchQuery?: string;
  isCreatorOnboardPending?: boolean;
  isInfluencerNewCostPending?: boolean;
  isInitialInfluencerCostPending?: boolean;
  isNewCommericalRequested?: boolean;
  isBrandNewCostPending?: boolean;
  isDeliverablesPending?: boolean;
  isConfirmationSent?: boolean;
  isConfirmed?: boolean;
  isCreatorDeclined?: boolean;
  isBrandDeclined?: boolean;
  briefId: string;
  sortByFollowers?: SORT_ORDER;
  page?: number;
  limit?: number;
  status?: string;
  statuses?: BreifInfluencerV2StatusEnum[];
  awaitingPurchase?: boolean;
  confirmationPending?: boolean;
  deleted?: boolean;
  isInitialBrandCostPending?: boolean;
  isPriceRequested?: boolean;
  requestPending?: boolean;
  isPrimaryContactAdded?: boolean;
  assignedPricingPocV2?: string[];
  isBrandFavourite?: boolean;
  sortByCreatedAt?: SORT_ORDER;
  sortByUpdatedAt?: SORT_ORDER;
  sortByBrandCost?: SORT_ORDER;
  brandCostStatus?: BRAND_COST_STATUS[];
  socialMediaPlatforms?: string[];

  deliverableV2Ids?: string[];
  primaryContact?: {
    type: string[]; // ["Agency", "Manager","Self"]
    agencyId?: string[];
  };

  influencerCost?: {
    date?: {
      from?: string;
      to?: string;
    };
    offerStatus?: string[];
    createdByType?: string[];
  };
  createdAt?: {
    from?: string;
    to?: string;
  };
  updatedAt?: {
    from?: string;
    to?: string;
  };
  isCostCheckedByPricing?: boolean;

  sortByDynamicFields?: {
    [key: string]: SORT_ORDER;
  };
  dynamicFields?: {
    [key: string]:
      | {
          equalTo: string[];
        }
      | {
          range?: {
            from?: number;
            to?: number;
          };
        };
  };
  pricingWhatsappReachout?: {
    statuses?: WhatsappReachoutStatus[];
  };

  isShortlisted?: boolean;

  pendingReviewActions?: {
    brand?: Partial<{
      agencySuggested: boolean;
      agencyDeclined: boolean;
      agencyAddedNotes: boolean;
      agencyAddNegoCost: boolean;
    }>;
    agency?: Partial<{
      brandShortlisted: boolean;
      brandDeclined: boolean;
      brandSuggested: boolean;
      brandPurchased: boolean;
      brandNegoRequested: boolean;
      brandChangedDeliverable: boolean;
      brandAddedNotes: boolean;
    }>;
  };
}

export interface IBriefInfluencerSearchRequestV2WithoutBriefId extends Omit<
  IBriefInfluencerSearchRequestV2,
  "briefId" | "status"
> {
  briefId?: string;
  brandIds?: string[];
  statuses?: BreifInfluencerV2StatusEnum[];
}

// export interface ICreateBriefInfluencerV2 {
//   briefId: string;
//   socialMediaObjectId: string;
//   influencerName: string;
//   socialMediaName: string;
//   followers: number;
//   estViews: number;
// }

// export interface IEditBriefInfluencerV2 {
//   briefInfluencerId: string;
//   influencerName?: string;
//   socialMediaName?: string;
//   followers?: number;
//   estViews?: number;
//   status?: string;
// }

export interface IGetBriefInfluencersV2Response {
  data: IBriefInfluencerV2[];
  pagination: IPagination;
}

export interface IGetBriefInfluencerV2Response extends IBriefInfluencerV2 {}

export interface ISearchFacetCount {
  _id: BreifInfluencerV2StatusEnum;
  count: number;
}

export interface ISearchFacets {
  status: ISearchFacetCount[];
  inBrief?: number;
  inExecution?: number;

  paymentTerms: ISearchFacetCount[];
  noOfCreatorsSuggested: number;

  noOfCreatorsWithPrices: number;

  isInfluencerCommercialPending: number;

  isInfluencerCommercialNegoRequested: number;

  isInfluencerCommercialReceived: number;
  isInfluencerCommercialNegoReceived: number;

  all: number;

  isOfferReceived: number;

  isNegotiating: number;
  isCreatorOnboardPending: number;

  isPrimaryContactNotAdded: number;

  isInitialInfluencerCostPending: number;

  isInitialBrandCostPending: number;

  isPriceRequested: number;

  isDeliverablesPending: number;

  isDeliverablesAttached: number;

  isCreatorDeclined: number;

  isBrandFavourite: number;

  isBrandDeclined: number;

  isBrandNewCostPending: number;

  isNewCommericalRequested: number;

  awaitingPurchase: number;

  confirmationPending: number;

  requestPending: number;

  deleted: number;

  assignedPricingPoc: {
    _id: string;
    user: {
      _id: string;
      firstName: string;
      lastName: string;
    };
  }[];
  assignedPricingPocV2: {
    _id: string;
    user: {
      _id: string;
      firstName: string;
      lastName: string;
    };
  }[];

  inReview?: number;
  inShortlisting?: number;

  pendingReviewActions?: {
    brand?: Partial<{
      agencySuggested: boolean;
      agencyDeclined: boolean;
      agencyAddedNotes: boolean;
      agencyAddNegoCost: boolean;
    }>;
    agency?: Partial<{
      brandShortlisted: boolean;
      brandDeclined: boolean;
      brandSuggested: boolean;
      brandPurchased: boolean;
      brandNegoRequested: boolean;
      brandChangedDeliverable: boolean;
      brandAddedNotes: boolean;
    }>;
  };
}

export enum PricingPocStatus {
  Pending = "Pending",
  Priced = "Priced",
  "Creator-Declined" = "Creator-Declined",
}

export interface IDeliverableFacet {
  _id: string;
}

export interface IDeliverableFacets {
  category: IDeliverableFacet[];
  type: IDeliverableFacet[];
  contentRightsDurations: IDeliverableFacet[];
}

export interface IFieldConfig {
  key: string;
  isSpecialField: boolean;
  category?: string;
  type?: CustomFieldType;
  defaultValue?: IDataFieldValue;
  description?: string;
  displayName?: string;
  _id?: string;
  dropdownOptions?: string[];
  isShared?: boolean;
}

export enum CustomFieldType {
  NUMBER = "number",
  STRING = "string",
  DROPDOWN = "dropdown",
  UPLOAD = "upload",
}

export type IDataFieldValue =
  number | string | string[] | Date | { url: string; type: UploadType }[];

export const enum UploadType {
  FILE = "file",
  LINK = "link",
}

export interface IFieldConfigResponse {
  performanceFields: IFieldConfig[];
  customFields: IFieldConfig[];
}

export interface ICampaignFieldConfigResponse {
  [key: string]: IFieldConfig[];
}

export type IBreifInfIdFacetsResult = {
  socialMedias: {
    _id: string;
    socialMedia: { _id: string; username: string; platform: string };
  }[];
  brands: { _id: string; brand: { _id: string; name: string } }[];
};

export interface IBriefInfluencerWhatsappReachout {
  messagedByUserId: string;
  messagedByUser: string;
  messagedByModelName: string;
  messagedByType: string;
  status: WhatsappReachoutStatus;
  deeplinkUrl?: string;
  watiReachoutId?: string;
  phoneNumber: string;
  countryCode: string;
  messagedAt: Date;
  failedReason?: string;
}

export const enum WhatsappReachoutStatus {
  Pending = "Pending",
  Processing = "Processing",
  Sent = "Sent",
  Delivered = "Delivered",
  Read = "Read",
  Replied = "Replied",
  Failed = "Failed",
  NotSent = "Not-Sent",
}

export enum BRAND_COST_STATUS {
  "First-Quote-Recieved" = "First-Quote-Recieved",
  "Negotiations-Requested" = "Negotiations-Requested",
  "New-Quote-Recieved" = "New-Quote-Recieved",
}

export enum OFFER_STATUS {
  "offer-yet-to-be-sent" = "offer-yet-to-be-sent",
  "offer-sent" = "offer-sent",
  "offer-accepted" = "offer-accepted",
  "negotiations-requested" = "negotiations-requested",
  "negotiations-sent" = "negotiations-sent",
  "negotiations-accepted" = "negotiations-accepted",
}
